import { makeStyles } from '@material-ui/core/styles';

/*
 * How wide the docked chat is.
 *
 * Named once because TWO rules depend on it: the panel itself, and the
 * padding the page adds so its own content stops short of the panel
 * instead of running underneath. They were separate numbers for one
 * render and the stepper immediately disappeared behind the chat.
 */
/*
 * The chat is now the page body, so it gets a reading width rather than a
 * rail width — wide enough for the assistant's example prompts to sit on
 * one line, capped so a wide monitor does not stretch them.
 */
/*
 * A reading column, not a slab. 56vw put the conversation across most of a
 * 1080p screen with the text pinned to either edge, which is why it read as
 * empty; a chat wants roughly the width of a paragraph.
 */
const DOCKED_CHAT_WIDTH = 'clamp(360px, 46vw, 760px)';

/*
 * The sticky programme header is 90px tall on desktop (ProgramHeaderBanner:
 * height 90px on md). Anything less than that tucks the panel UNDERNEATH it —
 * which is what made the chat look as though it had no top spacing at all.
 * Keep these two in step if that banner is ever resized.
 */
const DOCKED_CHAT_TOP = '90px';

/* Breathing room under the panel so it reads as a card, not a wall. */
const DOCKED_CHAT_GAP = '16px';
const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        background: 'var(--brand-bg, #fff)',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '8px 30px',
        backgroundColor: 'var(--brand-surface, #fff)',
        zIndex: 100,
        gap: '15px',
        flexShrink: 0,
        transition: 'padding 0.3s ease, border-bottom 0.3s ease',
    },
    headerCollapsed: {
        flexDirection: 'column !important',
        padding: '12px 30px',
        borderBottom: '1px solid #eee',
    },
    logo: {
        height: '41px',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            height: '32px',
        },
    },
    upgradeBtn: {
        background: 'linear-gradient(to right, rgba(236, 61, 57, 1), rgba(243, 96, 51, 1)) !important',
        color: '#ffff !important',
        padding: '7px 20px !important',
        borderRadius: '999px !important',
        fontWeight: '700 !important',
        fontSize: '15px !important',
        textTransform: 'none !important',
        boxShadow: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': {
            transform: 'scale(1.06)',
            boxShadow: '0 6px 18px rgba(243, 96, 51, 0.35) !important',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '5px 12px !important',
            fontSize: '12px !important',
        },
    },
    newChatBtn: {
        background: '#0097B2 !important',
        color: '#fff !important',
        padding: '2px 21px !important',
        borderRadius: '12px !important',
        fontWeight: '800 !important',
        fontSize: '17px !important',
        textTransform: 'none !important',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: '#00869e !important',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '2px 12px !important',
            fontSize: '13px !important',
        },
    },
    mainContainer: {
        display: 'flex',
        flex: 1,
        padding: '0',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },

    mobileToggle: {
        display: 'none',
    },
    mobileTab: {
        display: 'none',
    },

    /*
     * Slides 5-6: while the program is BUILDING the chat is the right half
     * of the screen, not a card floating over it. Flush to the top, bottom
     * and right edge; square on the inner edge so it meets the content as
     * one surface; a hairline instead of a drop shadow, because a shadow is
     * what makes a panel read as hovering.
     *
     * Composed WITH chatFloatingPanel, so it inherits the reveal animation
     * and the mobile full-screen breakpoint and only overrides geometry.
     */
    /*
     * With the left panel removed (Dr. Park, 10 Sep) the chat has no
     * neighbour, so it is centred as the page body rather than pinned to
     * the right edge. Capped, because a conversation stretched across a
     * wide monitor is unreadable; the rule below the header keeps it
     * clear of the stepper.
     */

    chatDockedPanel: {
        /*
         * Centred by auto side margins, NOT by translateX: the phase-* rules
         * that drive the reveal animation set transform:'none' on a more
         * specific selector, so a centring transform is dropped the instant
         * the panel opens and the chat lands in the right half instead.
         * Auto margins on a fixed box with a set width cannot be undone that
         * way.
         */
        left: '0 !important',
        right: '0 !important',
        marginLeft: 'auto !important',
        marginRight: 'auto !important',
        bottom: `${DOCKED_CHAT_GAP} !important`,
        top: `calc(${DOCKED_CHAT_TOP} + ${DOCKED_CHAT_GAP}) !important`,
        width: `${DOCKED_CHAT_WIDTH} !important`,
        maxWidth: 'calc(100% - 32px) !important',
        height: `calc(100vh - ${DOCKED_CHAT_TOP} - (${DOCKED_CHAT_GAP} * 2)) !important`,
        maxHeight: 'none !important',
        /*
         * NOT a card — Mehreen, 10 Sep: "remove the card view", the chat should
         * sit on the page rather than float on a panel. No radius, no shadow,
         * no border.
         *
         * But NOT transparent either. Transparent let the build cards behind it
         * show straight through the conversation, so text sat on top of other
         * text. Flat and opaque is the shape being asked for; see-through was
         * an accident of removing the card.
         */
        borderRadius: '0 !important',
        boxShadow: 'none !important',
        border: 'none !important',
        /*
         * The PAGE's own ground, taken literally from BuildingProgramView,
         * not --brand-bg. That token resolves to #FFFBF7 while the page renders
         * #FAF8F4 — four points apart, which is exactly enough to read as a
         * white card sitting on cream. The fallback never applied because the
         * token IS defined; it is just a different colour.
         */
        /*
         * The SAME token the page wrapper paints — var(--brand-bg) — so the
         * panel and the page behind it are identical by construction, whatever
         * the branding sets.
         *
         * A literal colour was tried first, copied from BuildingProgramView.
         * That is a different screen: pageWrapper is what sits behind the chat
         * before a build starts, and it uses this token. The literal was four
         * points darker, which is exactly the faint edge that kept showing.
         */
        /*
         * No background at all — Mehreen, 11 Sep: "isk bg color bilkul remove
         * kr dain to match with background of page".
         *
         * Matching a colour to the page means keeping two values in step
         * forever; painting nothing means they cannot drift. Safe now that the
         * chat closes itself when a build starts, so there are no build cards
         * left behind it to read through.
         */
        background: 'transparent !important',
        [theme.breakpoints.down('sm')]: {
            left: '0 !important',
            transform: 'none',
            width: 'auto !important',
            height: 'auto !important',
            borderRight: 'none',
        },
    },

    /*
     * Opaque, for the build screen only.
     *
     * The panel is transparent so it matches the page. That is right until a
     * build is running: the five progress cards sit directly behind it, and
     * reopening the chat then puts the conversation on top of them — two
     * layers of text in the same space.
     *
     * Applied ONLY while isProgramBuilding, so nothing else about the chat
     * changes.
     */
    chatPanelSolid: {
        background: 'var(--brand-bg, #fff) !important',
    },

    chatFloatingPanel: {
        position: 'fixed',
        right: '24px',
        bottom: '24px',
        width: '400px',
        height: 'calc(100vh - 48px)',
        maxHeight: '720px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
        overflow: 'hidden',
        zIndex: 1301,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--brand-surface, #fff)',
        border: '1px solid var(--brand-border, #EBEBEB)',
        willChange: 'clip-path, transform',
        [theme.breakpoints.down('sm')]: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: 'auto',
            height: 'auto',
            maxHeight: 'none',
            borderRadius: 0,
        },
        /*
         * The panel grows out of its own bottom edge and wipes upward.
         *
         * Anchored bottom-CENTRE, not the old bottom-right corner: that corner
         * used to sit on the toggle button, but the panel is centred now, so
         * collapsing toward it made the chat visibly travel across the gap
         * between the two.
         */
        // Bottom centre: the panel grows upward out of its own footprint,
        // which is the direction the toggle sits in.
        transformOrigin: 'center bottom',

        /*
         * Scale-and-fade, the way a centred dialog opens everywhere else.
         *
         * The clip-path wipe came from the days of a bottom-right widget, where
         * a panel growing out of its own edge made sense. On a tall centred
         * panel the same wipe reads as a curtain and draws attention to the
         * motion instead of the content. A short rise from 0.98 is what a
         * centred chat is expected to do: present, not performed.
         */
        /*
         * Rises from its own bottom edge and settles — the motion a centred
         * dialog is expected to have. Short enough to read as the panel
         * arriving rather than as an animation being played at you.
         */
        '&.phase-pre-open': {
            clipPath: 'inset(100% 0 0 0 round 16px)',
            transform: 'translateY(8px)',
            opacity: 0,
            transition: 'none',
        },

        '&.phase-opening, &.phase-sliding, &.phase-open': {
            transform: 'translateY(0) scale(1)',
            opacity: 1,
            transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1),'
                + ' opacity 160ms ease-out',
        },

        /* Quicker on the way out — leaving should not be a performance. */
        '&.phase-closing': {
            transform: 'scale(0.98) translateY(6px)',
            opacity: 0,
            transition: 'transform 180ms cubic-bezier(0.4, 0, 1, 1),'
                + ' opacity 140ms ease-in',
        },

        /* Motion is decoration here; the end state is the content. */
        '@media (prefers-reduced-motion: reduce)': {
            '&.phase-pre-open, &.phase-opening, &.phase-sliding, &.phase-open': {
                clipPath: 'none',
                transform: 'none',
                opacity: 1,
                transition: 'none',
            },
            '&.phase-closing': { opacity: 0, transition: 'none' },
        },
    },
    chatToggleBtn: {
        position: 'fixed',
        right: '24px',
        bottom: '24px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        background: 'var(--brand-surface, #fff)',
        border: '2.5px solid #FF6900',
        boxShadow: '0 4px 18px rgba(255, 105, 0, 0.35)',
        cursor: 'pointer',
        zIndex: 1248,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0px',
        boxSizing: 'border-box',
        willChange: 'transform, opacity',
        /* Idle: spring-bounce in — starts immediately when chatAnimPhase → idle */
        '&.btn-idle': {
            opacity: 1,
            pointerEvents: 'auto',
            transition: [
                'transform 2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                'opacity 1.12s ease',
                'box-shadow 0.6s',
            ].join(', '),
        },
        /* Hover only in idle state */
        '&.btn-idle:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 6px 22px rgba(255, 105, 0, 0.52)',
        },
    },
    resizerLine: {
        width: '4px',
        height: '40px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        borderRadius: '2px',
        transition: 'background-color 0.2s',
    },
    sidebar: {
        width: '100%',
        height: '100%',
        /*
         * THE white card.
         *
         * This is ChatSidebar's own root, inside the panel — so it painted white
         * over the panel no matter what the panel was set to. Every other
         * surface was made to inherit and the card persisted because this one
         * was still filling. It only ever looks like a separate plate; the panel
         * already provides the ground.
         *
         * (TeacherDashboardLayout also uses a class called sidebar, but from
         * TeacherDashboardStyles — a different sheet, unaffected by this.)
         */
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        minHeight: 0,
        position: 'relative',
        flexShrink: 0,
        alignSelf: 'stretch',
    },
    sidebarHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px 14px 16px',
        borderBottom: '1px solid var(--brand-border, #F0F0F0)',
        flexShrink: 0,
        minWidth: 0,
        overflow: 'hidden',
    },
    sidebarBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
        flex: 1,
    },
    sidebarBrandIcon: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        backgroundColor: 'var(--brand-surface, #fff)',
        border: '2px solid #FF6900',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        padding: '2px',
    },
    sidebarBrandText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
    },
    sidebarBrandName: {
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--brand-text, #111)',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    sidebarBrandSub: {
        fontSize: '12px',
        color: 'var(--brand-text-muted, #999)',
        lineHeight: 1.3,
    },
    sidebarLogoContainer: {
        flexShrink: 0,
    },
    chatContainerWrap: {
        flex: 1,
        // Inherit the panel's cream. A white plate here IS the card.
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 16px 0 16px',
        overflow: 'hidden',
        overflowX: 'hidden',
        minHeight: 0,
    },
    sidebarTitle: {
        fontSize: '22px',
        fontWeight: 800,
        color: '#0097B2',
        marginBottom: '40px',
        lineHeight: 1.3,
    },
    sidebarContent: {
        flex: 1,
        overflowY: 'auto',
        paddingRight: '6px',
        marginBottom: '15px',
        minHeight: 0,
        '&::-webkit-scrollbar': {
            width: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.08)',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
    },
    instructionBanner: {
        color: '#fff',
        padding: '12px 40px',
        textAlign: 'center',
        fontSize: '18px',
        lineHeight: 1.5,
        marginBottom: '0px',
        [theme.breakpoints.down('sm')]: {
            padding: '15px 20px',
            fontSize: '14px',
        },
        '& strong': {
            fontWeight: 800,
        }
    },
    stepItem: {
        marginBottom: '32px',
        position: 'relative',
        paddingLeft: '15px',
        '&::before': {
            content: "''",
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: '#0097B2',
            borderRadius: '10px',
            opacity: 0.3,
        }
    },
    stepItemActive: {
        '&::before': {
            opacity: 1,
            boxShadow: '0 0 10px rgba(0, 151, 178, 0.4)',
        }
    },
    chatHistory: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        paddingRight: '4px',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#eee', borderRadius: '10px' },
    },
    didYouKnow: {
        marginTop: '30px',
        padding: '20px',
        backgroundColor: 'rgba(0, 151, 178, 0.05)',
        borderRadius: '16px',
        fontSize: '14px',
        color: '#595959',
        fontStyle: 'italic',
        lineHeight: 1.5,
        border: '1px dashed rgba(0, 151, 178, 0.2)',
    },
    chatInputWrapper: {
        // Anchor for the send button, which sits INSIDE the pill rather than
        // below it — the arrow was floating outside the field entirely.
        position: 'relative',
        // Invisible strip; the pill inside it is the only lifted surface,
        // which is what the reference shows.
        backgroundColor: 'transparent',
        borderTop: 'none',
        padding: '8px 16px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flexShrink: 0,
    },
    chatInputRow: {
        // Bottom-right of the pill, clear of its rounded corner.
        position: 'absolute',
        right: '26px',
        bottom: '26px',
        zIndex: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    chatInput: {
        width: '100%',
        /*
         * A rounded pill lifted off the cream — the one place the eye is meant
         * to land, and what makes this read as an ordinary centred chatbot
         * rather than a panel with a form stuck to the bottom.
         */
        '& .MuiInputBase-root': {
            padding: '10px 16px',
            // Room for the send button so typing never runs under it.
            paddingRight: '56px',
            borderRadius: '24px',
            backgroundColor: 'var(--brand-surface, #fff)',
            border: '1px solid var(--brand-border, #EDE7E1)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            fontSize: '14px',
            color: '#333',
            '& textarea': {
                '&::placeholder': {
                    color: '#999 !important',
                },
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
    },
    sendBtn: {
        backgroundColor: '#1a1a1a !important',
        color: '#fff !important',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        borderRadius: '50% !important',
        padding: '0 !important',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: '#333 !important',
        },
        '&.Mui-disabled': {
            backgroundColor: '#ccc !important',
        },
    },
    // chatHistory: {
    //     display: 'flex',
    //     flexDirection: 'column',
    //     gap: '16px',
    //     paddingBottom: '10px',
    // },
    messageRow: {
        display: 'flex',
        width: '100%',
    },
    userRow: {
        justifyContent: 'flex-end',
    },
    botRow: {
        justifyContent: 'flex-start',
    },
    botMessage: {
        width: '100%',
        backgroundColor: 'transparent',
        borderRadius: 0,
        padding: '0',
        color: 'var(--brand-text, #1a1a1a)',
        fontSize: '14px',
        lineHeight: 1.6,
        position: 'relative',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        boxSizing: 'border-box',
    },
    botMsgContainer: {
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    userMessage: {
        maxWidth: '85%',
        backgroundColor: '#FF6900',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 1.5,
        borderRadius: '12px',
        padding: '10px 14px',
    },
    contentArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '0',
        overflow: 'hidden',
        boxSizing: 'border-box',
        alignSelf: 'stretch',
    },
    contentBody: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    programTitle: {
        textAlign: 'center',
        fontSize: '28px !important',
        fontWeight: 'bold !important',
        color: '#FF6900',
        margin: '0 0 4px 0 !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: '24px !important',
        }
    },
    collapsedTitle: {
        fontSize: '22px !important',
        fontWeight: 'bold !important',
        color: '#FF6900',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '500px',
    },
    programSubtitle: {
        textAlign: 'center',
        fontSize: '18px !important',
        fontWeight: 800,
        color: '#000',
        marginBottom: '10px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '18px !important',
            padding: '0 15px',
        }
    },
    clickPreviewText: {
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 800,
        color: '#0097B2',
        marginBottom: '15px',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '15px',
        }
    },
    tabsWrapper: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '15px',
        marginTop: '8px',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
            padding: '0 10px',
            gap: '8px',
        }
    },
    tabButton: {
        borderRadius: '37px !important',
        padding: '2px 24px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '18px !important',
        color: '#666 !important',
        backgroundColor: 'transparent !important',
        border: '1px solid #ccc !important',
        [theme.breakpoints.down('sm')]: {
            padding: '2px 15px !important',
            fontSize: '14px !important',
        },
        '&:hover': {
            backgroundColor: '#f5f5f5 !important',
            color: '#0097B2 !important',
            borderColor: '#0097B2 !important',
        },
    },
    tabButtonCollapsed: {
        fontSize: '15px !important',
        padding: '2px 14px !important',
    },
    activeTab: {
        backgroundColor: '#0097B2 !important',
        color: '#fff !important',
        border: '2px solid #0097B2 !important',
    },
    innerContent: {
        flex: 1,
        borderRadius: '0px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxSizing: 'border-box',
        minHeight: 0,
    },
    creatingNowWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#fff',
    },
    creatingNowTitle: {
        fontSize: '28px !important',
        fontWeight: 'bold !important',
        marginTop: '30px !important',
    },
    // Inner Tab Content Styles
    previewScroll: {
        flex: 1,
        overflowY: 'auto',
        padding: '30px',
        [theme.breakpoints.down('sm')]: {
            padding: '15px',
        },
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#f0f0f0',
            borderRadius: '10px',
        },
    },
    dummyImageContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--brand-surface, #f8fafc)',
    },
    dummyImage: {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '20px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
    },
    // Outline Graphical Styles
    outlineGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
        marginBottom: '40px',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
            gap: '16px',
            marginBottom: '24px',
        },
    },
    outlineCard: {
        padding: '30px',
        borderRadius: '24px',
        backgroundColor: '#FE686F',
        color: '#fff',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        transition: 'transform 0.3s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
        }
    },
    outlineCardBlue: {
        backgroundColor: '#0097B2',
    },
    outlineCardOrange: {
        backgroundColor: '#FF8A00',
    },
    outlineCardTitle: {
        fontSize: '16px',
        fontWeight: 600,
        marginBottom: '10px',
        opacity: 0.9,
    },
    outlineCardValue: {
        fontSize: '24px',
        fontWeight: 900,
    },
    overviewBox: {
        backgroundColor: 'rgba(254, 104, 111, 0.04)',
        padding: '30px',
        borderRadius: '24px',
        border: '1px solid rgba(254, 104, 111, 0.1)',
        marginBottom: '40px',
        [theme.breakpoints.down('sm')]: {
            padding: '16px',
            borderRadius: '16px',
            marginBottom: '24px',
        },
    },
    overviewLabel: {
        color: '#FE686F',
        fontWeight: 800,
        fontSize: '20px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        '&::before': {
            content: "''",
            width: '6px',
            height: '24px',
            backgroundColor: '#FE686F',
            borderRadius: '10px',
        }
    },
    overviewText: {
        fontSize: '17px',
        lineHeight: 1.7,
        color: '#525252',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
        },
    },
    moduleHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        cursor: 'pointer',
        padding: '16px',
        borderRadius: '16px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.02)',
        }
    },
    moduleTag: {
        backgroundColor: '#0097B2',
        color: '#fff',
        padding: '4px 12px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 700,
    },
    learningBanner: {
        backgroundColor: '#333',
        color: '#fff',
        padding: '24px 60px',
        textAlign: 'center',
        fontSize: '18px',
        lineHeight: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            padding: '15px 20px',
            fontSize: '14px',
        }
    },
    learningSplitBody: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: 'var(--brand-surface, #fff)',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            overflowY: 'auto',
        }
    },
    learningSidebar: {
        width: '320px',
        backgroundColor: 'var(--brand-surface, #F3F4F6)',
        borderRight: '1px solid var(--brand-border, #E5E7EB)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        padding: '25px 20px',
        boxSizing: 'border-box',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxHeight: '300px',
            borderRight: 'none',
            borderBottom: '1px solid var(--brand-border, #E5E7EB)',
        }
    },
    learningMain: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'var(--brand-surface, #fff)',
        padding: '40px 60px',
        boxSizing: 'border-box',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            padding: '20px 15px',
            minHeight: '400px',
            overflow: 'visible',
        }
    },
    // New Hierarchy Styles for learning tab
    moduleItem: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#000',
        marginBottom: '15px',
        marginTop: '10px',
    },
    unitItem: {
        fontSize: '15px',
        fontWeight: 'bold',
        color: '#000',
        marginLeft: '15px',
        marginBottom: '12px',
        marginTop: '15px',
    },
    sessionItem: {
        marginLeft: '30px',
        marginBottom: '15px',
    },
    sessionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
    },
    sessionTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#4B5563',
    },
    sessionDotList: {
        listStyle: 'none',
        padding: 0,
        margin: '5px 0 0 15px',
    },
    sessionDotItem: {
        fontSize: '13px',
        color: '#6B7280',
        position: 'relative',
        paddingLeft: '12px',
        marginBottom: '4px',
        '&::before': {
            content: "''",
            position: 'absolute',
            left: 0,
            top: '8px',
            width: '4px',
            height: '4px',
            backgroundColor: '#000',
            borderRadius: '50%',
        }
    },
    viewBtn: {
        backgroundColor: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '25px !important',
        padding: '3px 0 !important',
        textTransform: 'none !important',
        fontWeight: 'bold !important',
        fontSize: '12px !important',
        minWidth: '70px !important',
        textAlign: 'center',
    },
    lockedBtn: {
        backgroundColor: '#000 !important',
        color: '#fff !important',
        borderRadius: '25px !important',
        padding: '3px 0 !important',
        textTransform: 'none !important',
        fontWeight: 'bold !important',
        fontSize: '12px !important',
        opacity: '1 !important',
        minWidth: '70px !important',
        textAlign: 'center',
    },
    learningPreviewContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    slideTitle: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#FF6900',
        textAlign: 'center',
        marginBottom: '30px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px',
            marginBottom: '15px',
        }
    },
    videoPlaceholder: {
        width: '100%',
        flex: 1,
        backgroundColor: '#000',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
    },
    progressLine: {
        width: '100%',
        height: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '10px',
        marginBottom: '40px',
        position: 'relative',
    },
    progressFill: {
        width: '40%',
        height: '100%',
        backgroundColor: '#FF6900',
        borderRadius: '10px',
        position: 'relative',
    },
    progressHandle: {
        position: 'absolute',
        right: '-15px',
        top: '-15px',
        width: '40px',
        height: '40px',
        cursor: 'pointer',
    },
    learningIntro: {
        display: 'none', // Hide old intro as per new design
    },
    // Assessment View Styles
    assessContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '60px',
        [theme.breakpoints.down('sm')]: {
            padding: '20px',
        },
    },
    assessIntroImg: {
        width: '320px',
        marginBottom: '40px',
        filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.1))',
    },
    assessTitle: {
        fontSize: '36px',
        fontWeight: 900,
        color: '#FE686F',
        textAlign: 'center',
        marginBottom: '20px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '24px',
            marginBottom: '12px',
        },
    },
    assessSubtitle: {
        fontSize: '18px',
        color: '#8c8c8c',
        textAlign: 'center',
        maxWidth: '600px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px',
        },
    },
    startAssessBtn: {
        marginTop: '50px',
        backgroundColor: '#FE686F',
        color: '#fff',
        padding: '14px 50px',
        borderRadius: '16px',
        fontWeight: 800,
        fontSize: '18px',
        boxShadow: '0 10px 25px rgba(254, 104, 111, 0.3)',
        '&:hover': {
            backgroundColor: '#ff7a80',
        }
    },
    // Legacy DQPro Sidebar Styles
    moduleCard: {
        marginBottom: '16px !important',
        borderRadius: '16px !important',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05) !important',
        border: '1px solid #eee !important',
        overflow: 'hidden',
    },
    moduleCardContent: {
        padding: '16px 20px !important',
    },
    lessonContainer: {
        width: '100%',
        padding: '10px 15px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(238, 102, 40, 0.05)',
        }
    },
    sectionRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    sectionText: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: '#5A5A5A',
        flex: 1,
    },
    sectionSegmentRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px 8px 30px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(238, 102, 40, 0.05)',
        }
    },
    segmentText: {
        fontSize: '13px !important',
        color: '#4B5563',
    },
    activeSegment: {
        color: '#ED6628 !important',
        fontWeight: 'bold !important',
    },
    plansDialog: {
        '& .MuiPaper-root': {
            borderRadius: '40px',
            padding: '40px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                borderRadius: '30px',
                padding: '30px 15px',
                margin: '10px !important',
                width: 'calc(100% - 20px) !important',
            }
        }
    },
    planTitle: {
        fontSize: '32px !important',
        fontWeight: 'bold !important',
        textAlign: 'center',
        marginBottom: '20px !important',
        color: '#000',
        [theme.breakpoints.down('sm')]: {
            fontSize: '23px !important',
            marginBottom: '15px !important',
        }
    },
    planGrid: {
        display: 'flex',
        gap: '25px',
        justifyContent: 'center',
        padding: '20px 0',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
            gap: '40px',
            padding: '10px 0 40px 0',
        }
    },
    planCard: {
        flex: 1,
        borderRadius: '20px',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        position: 'relative',
        minHeight: '600px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.04)',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minHeight: 'auto',
            padding: '28px 20px',
            borderRadius: '20px',
            flex: 'none',
        }
    },
    orangePlan: {
        background: 'linear-gradient(135deg, rgba(0, 84, 221, 0.05) 0%, rgba(255, 0, 34, 0.05) 100%) !important',
        '& $planDescription': { color: '#6A8EC9 !important' },
        '& $planFeatureList li::before, & $planFeatureList p::before': { color: '#6A8EC9 !important' },
    },
    tealPlan: {
        background: 'linear-gradient(135deg, rgba(51, 221, 0, 0.05) 0%, rgba(238, 255, 0, 0.05) 100%) !important',
        border: '2px solid rgba(255, 105, 0, 1) !important',
        '& $planDescription': { color: '#FF6900 !important' },
        '& $planFeatureList li::before, & $planFeatureList p::before': { color: '#FF6900 !important' },
    },
    bluePlan: {
        background: 'linear-gradient(135deg, rgba(221, 0, 200, 0.05) 0%, rgba(255, 200, 0, 0.05) 100%) !important',
        '& $planDescription': { color: '#C91C6E !important' },
        '& $planFeatureList li::before, & $planFeatureList p::before': { color: '#C91C6E !important' },
    },
    recommendedBadge: {
        position: 'absolute',
        top: '0px',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(255, 105, 0, 1)',
        color: '#fff',
        padding: '8px 24px',
        borderRadius: '999px',
        fontWeight: 700,
        fontSize: '14px',
        zIndex: 5,
        width: 'max-content',
        boxShadow: '0 4px 12px rgba(255, 105, 0, 0.25)',
    },
    planHeader: {
        fontSize: '22px !important',
        fontWeight: '800 !important',
        marginBottom: '8px !important',
        color: '#111',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px !important',
        }
    },
    planSubHeader: {
        fontSize: '15px !important',
        fontWeight: 'bold !important',
        color: '#fff',
        marginBottom: '13px !important',
        lineHeight: 1.4,
        padding: '0 10px',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '14px !important',
            minHeight: 'auto',
            marginBottom: '15px !important',
        }
    },
    planDescription: {
        fontSize: '14px !important',
        fontWeight: '700 !important',
        color: 'rgba(255, 105, 0, 1)',
        marginBottom: '20px !important',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        padding: 0,
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        }
    },
    planFeatureList: {
        textAlign: 'left',
        width: '100%',
        marginBottom: 'auto',
        minHeight: '180px',
        padding: 0,
        [theme.breakpoints.down('sm')]: {
            minHeight: 'auto',
            marginBottom: '20px',
        },
        '& p': {
            fontSize: '14px',
            fontWeight: 400,
            margin: '10px 0',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            color: '#1a1a1a',
            lineHeight: 1.5,
            [theme.breakpoints.down('sm')]: {
                fontSize: '13px',
                margin: '8px 0',
            },
            '&::before': {
                content: '"✓"',
                color: 'rgba(255, 105, 0, 1)',
                fontWeight: 800,
                fontSize: '15px',
                lineHeight: 1.4,
            }
        }
    },
    planPrice: {
        fontSize: '26px !important',
        fontWeight: '800 !important',
        color: '#111',
        marginTop: '8px !important',
        marginBottom: '8px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: '22px !important',
        }
    },
    planSubPrice: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#111',
        marginBottom: '30px',
    },
    planSelectBtn: {
        width: '80%',
        background: 'linear-gradient(to right, rgba(236, 61, 57, 1), rgba(243, 96, 51, 1)) !important',
        color: '#fff !important',
        borderRadius: '10px !important',
        fontSize: '15px !important',
        fontWeight: '600 !important',
        padding: '8px 0 !important',
        marginTop: '16px !important',
        textTransform: 'none !important',
        marginLeft: 'auto !important',
        marginRight: 'auto !important',
        boxShadow: 'none !important',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 4px 12px rgba(243, 96, 51, 0.3) !important',
        },
        '&.Mui-disabled': {
            background: '#e5e7eb !important',
            color: '#9ca3af !important',
        }
    },
    // Publish Dialog Styles
    publishDialogPaper: {
        borderRadius: '35px !important',
        padding: '50px 40px !important',
        maxWidth: '750px !important',
        width: '100% !important',
        textAlign: 'center',
        boxShadow: '0 10px 50px rgba(0,0,0,0.15) !important',
    },
    publishDialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    publishDialogTitle: {
        fontSize: '22px !important',
        color: '#222',
        lineHeight: '1.4 !important',
        marginBottom: '20px !important',
    },
    publishDialogHighlight: {
        fontWeight: '900',
        color: '#000',
    },
    publishDialogSubtitle: {
        fontSize: '20px !important',
        color: '#333',
        marginBottom: '40px !important',
    },
    publishDialogActions: {
        display: 'flex',
        gap: '20px',
        justifyContent: 'center',
    },
    publishDialogBtnGoBack: {
        background: '#0097b2 !important',
        color: '#fff !important',
        borderRadius: '12px !important',
        padding: '12px 40px !important',
        fontSize: '18px !important',
        fontWeight: '800 !important',
        textTransform: 'none !important',
        minWidth: '150px',
        boxShadow: '0 4px 10px rgba(0, 151, 178, 0.2)',
    },
    publishDialogBtnPublish: {
        background: '#f54814 !important',
        color: '#fff !important',
        borderRadius: '12px !important',
        padding: '12px 40px !important',
        fontSize: '18px !important',
        fontWeight: '800 !important',
        textTransform: 'none !important',
        minWidth: '150px',
        boxShadow: '0 4px 10px rgba(245, 72, 20, 0.2)',
    },

    /* ── Frameworks alignment message ── */
    frameworksMsgWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        padding: '12px 14px',
        backgroundColor: 'var(--brand-surface, #fff)',
    },
    frameworksMsgLabel: {
        fontSize: '11px',
        fontWeight: 700,
        color: '#999',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    frameworksPillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        alignItems: 'flex-start',
        width: '100%',
    },
    frameworkPill: {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: 'rgba(183, 211, 52, 1)',
        color: '#3a4a00',
        border: 'none',
        borderRadius: '20px',
        padding: '4px 12px',
        fontSize: '13px',
        fontWeight: 600,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
    },
    frameworkMoreBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        backgroundColor: 'rgba(183, 211, 52, 0.5)',
        border: 'none',
        cursor: 'pointer',
        color: '#3a4a00',
        fontSize: '13px',
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: '20px',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: 'rgba(183, 211, 52, 0.8)',
        },
    },

    /* ── Time-options chip card ── */
    timeOptionsMsgWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    timeOptionsMsgTitle: {
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--brand-text, #1a1a1a)',
        lineHeight: 1.4,
        marginBottom: '2px',
    },
    timeOptionsMsgSubtitle: {
        fontSize: '13px',
        color: 'var(--brand-text-muted, #888)',
        lineHeight: 1.4,
        marginBottom: '4px',
    },
    timeOptionsMsgHint: {
        fontSize: '12px',
        color: '#9CA3AF',
        fontStyle: 'italic',
    },
    timeOptionsChipsRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    timeOptionsChip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        backgroundColor: 'var(--brand-surface, #fff)',
        color: 'var(--brand-text, #1a1a1a)',
        border: '1px solid #FF6900',
        borderRadius: '999px',
        padding: '11px 16px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxSizing: 'border-box',
        '&:hover': {
            backgroundColor: '#FF6900',
            color: '#fff',
        },
    },
    timeOptionsChipOwn: {
        backgroundColor: '#F3F4F6',
        color: '#555',
        border: '1.5px solid #D1D5DB',
        '&:hover': {
            backgroundColor: '#E5E7EB',
            color: '#111',
            border: '1.5px solid #9CA3AF',
        },
    },
    timeOptionsChipSelected: {
        backgroundColor: '#FF6900 !important',
        color: '#fff !important',
        border: '1.5px solid #FF6900 !important',
        cursor: 'default !important',
    },
    timeOptionsChipDisabled: {
        opacity: 0.45,
        cursor: 'default !important',
        pointerEvents: 'none',
    },
}));

export default useStyles;
